// SignInModalComponent.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Form,
  Input,
  Divider,
  Button,
  Checkbox,
  Select,
  Tabs,
  Alert,
  Typography,
} from 'antd';
import { GoogleOutlined, MailOutlined, LockOutlined } from '@ant-design/icons';
import './AuthModal.css';
import {
  SIGNUP_SOURCE_OPTIONS,
  sanitizeSignupSource,
  sanitizeSignupSourceDetail,
  signupSourceAcceptsDetail,
} from './signupAttribution';

const { Title, Text } = Typography;

interface Props {
  isLoginModalVisible: boolean;
  handleLoginCancel: () => void;
  onFormSubmit: (values: {
    username: string;
    password: string;
    actionType: string;
    emailConsent?: boolean;
    referralCode?: string;
    signupSource?: string;
    signupSourceDetail?: string;
  }) => void;
  signInWithGoogle: (options?: {
    requireConsent?: boolean;
    referralCode?: string;
    signupSource?: string;
    signupSourceDetail?: string;
  }) => void;
  error: string | null;
  resetPassword: (email: string) => void;
  showResetModal: () => void;
  setIsResetModalVisible: (value: boolean) => void;
  loading: boolean;
  message: string | null;
  checkoutPlan?: string | null;
  entryPoint?: 'default' | 'candidate-finder';
}

const REFERRAL_STORAGE_KEY = 'scribeshadow_referral_code';

const sanitizeReferralCode = (value: string): string =>
  value
    ? value
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 15)
    : '';

const SignInModalComponent: React.FC<Props> = ({
  isLoginModalVisible,
  handleLoginCancel,
  onFormSubmit,
  signInWithGoogle,
  error,
  setIsResetModalVisible,
  loading,
  message,
  checkoutPlan,
  entryPoint = 'default',
}) => {
  // default to 'signup' if coming from checkout or when a stored referral code is present
  const [activeTab, setActiveTab] = useState<string>(() => {
    if (checkoutPlan) {
      return 'signup';
    }
    if (typeof window !== 'undefined') {
      try {
        const stored = window.localStorage.getItem(REFERRAL_STORAGE_KEY);
        if (stored && sanitizeReferralCode(stored)) {
          return 'signup';
        }
      } catch (error) {
        // ignore storage access issues
      }
    }
    return 'signin';
  });
  // Separate forms per tab to avoid collisions and re-mount issues
  const [signInForm] = Form.useForm();
  const [signUpForm] = Form.useForm();

  const [googleConsentVisible, setGoogleConsentVisible] = useState(false);
  const [isSignUpFormValid, setIsSignUpFormValid] = useState(false);
  const [signupSource, setSignupSource] = useState<string | undefined>();
  const autoReferralTabRef = useRef(false);

  // if checkoutPlan prop changes (e.g. user navigates into checkout),
  // switch to Sign Up tab
  useEffect(() => {
    if (checkoutPlan) {
      setActiveTab('signup');
      autoReferralTabRef.current = true;
    }
  }, [checkoutPlan]);

  useEffect(() => {
    if (isLoginModalVisible && entryPoint === 'candidate-finder') {
      setActiveTab('signup');
      autoReferralTabRef.current = true;
    }
  }, [entryPoint, isLoginModalVisible]);

  const showResetModal = () => {
    setIsResetModalVisible(true);
  };

  // Pure validator: no side-effects like setFieldsValue (prevents re-render/focus loss)
  const validateEmail = (_: any, value: string) => {
    if (!value) {
      return Promise.reject('Please input your email!');
    }
    const sanitized = value.trim().toLowerCase();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(sanitized)) {
      return Promise.reject('Please enter a valid email address!');
    }
    return Promise.resolve();
  };

  // Sanitize email as the user types, without extra form updates
  const emailSanitizer = (e: any) => {
    const val = e?.target?.value ?? '';
    return String(val).trim().toLowerCase();
  };

  const handleFormSubmit = (values: any) => {
    const {
      signupSource: rawSignupSource,
      signupSourceDetail: rawSignupSourceDetail,
      ...remainingValues
    } = values;
    const sanitizedReferral = sanitizeReferralCode(values.referralCode || '');
    const sanitizedSignupSource = sanitizeSignupSource(rawSignupSource);
    const sanitizedSignupSourceDetail = sanitizeSignupSourceDetail(
      sanitizedSignupSource,
      rawSignupSourceDetail,
    );
    onFormSubmit({
      ...remainingValues,
      referralCode: sanitizedReferral || undefined,
      ...(activeTab === 'signup' && sanitizedSignupSource
        ? {
            signupSource: sanitizedSignupSource,
            ...(sanitizedSignupSourceDetail
              ? { signupSourceDetail: sanitizedSignupSourceDetail }
              : {}),
          }
        : {}),
      actionType: activeTab === 'signin' ? 'login' : 'register',
    });
  };

  const handleGoogleSignIn = () => {
    if (activeTab === 'signup') {
      const selectedSource = sanitizeSignupSource(signUpForm.getFieldValue('signupSource'));
      if (!selectedSource) {
        void signUpForm.validateFields(['signupSource']).catch(() => undefined);
        return;
      }
      setGoogleConsentVisible(true);
    } else {
      signInWithGoogle({ requireConsent: false });
    }
  };

  const handleGoogleSignUpWithConsent = () => {
    const referralCodeValue = sanitizeReferralCode(signUpForm.getFieldValue('referralCode') || '');
    const selectedSource = sanitizeSignupSource(signUpForm.getFieldValue('signupSource'));
    if (!selectedSource) {
      setGoogleConsentVisible(false);
      void signUpForm.validateFields(['signupSource']).catch(() => undefined);
      return;
    }
    const sourceDetail = sanitizeSignupSourceDetail(
      selectedSource,
      signUpForm.getFieldValue('signupSourceDetail'),
    );
    signInWithGoogle({
      requireConsent: true,
      referralCode: referralCodeValue || undefined,
      signupSource: selectedSource,
      ...(sourceDetail ? { signupSourceDetail: sourceDetail } : {}),
    });
    setGoogleConsentVisible(false);
  };

  const checkSignUpFormValidity = () => {
    const requiredFields = ['username', 'password', 'emailConsent', 'signupSource'];
    const values = signUpForm.getFieldsValue(requiredFields);
    const errors = signUpForm.getFieldsError(requiredFields);
    const hasErrors = errors.some(({ errors }) => errors.length > 0);
    const allFieldsFilled =
      values.username && values.password && values.emailConsent && sanitizeSignupSource(values.signupSource);
    setIsSignUpFormValid(!hasErrors && allFieldsFilled);
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    autoReferralTabRef.current = true;
  };

  useEffect(() => {
    if (!isLoginModalVisible) {
      autoReferralTabRef.current = false;
      return;
    }
    if (autoReferralTabRef.current || activeTab === 'signup') {
      return;
    }
    try {
      const stored = window.localStorage.getItem(REFERRAL_STORAGE_KEY);
      if (stored && sanitizeReferralCode(stored)) {
        setActiveTab('signup');
        autoReferralTabRef.current = true;
      }
    } catch (error) {
      // ignore storage errors
    }
  }, [isLoginModalVisible, activeTab]);

  useEffect(() => {
    if (!isLoginModalVisible || activeTab !== 'signup') return;
    try {
      const stored = window.localStorage.getItem(REFERRAL_STORAGE_KEY);
      if (stored) {
        const sanitized = sanitizeReferralCode(stored);
        if (sanitized) {
          signUpForm.setFieldsValue({ referralCode: sanitized });
        }
      }
    } catch (error) {
      // ignore storage errors
    }
  }, [isLoginModalVisible, activeTab, signUpForm]);

  useEffect(() => {
    if (!isLoginModalVisible || activeTab !== 'signup') return;
    checkSignUpFormValidity();
  }, [isLoginModalVisible, activeTab]);

  const modalTitle = (
    <div className="auth-modal-title">
      <span className="auth-modal-kicker">
        {checkoutPlan ? 'Checkout' : entryPoint === 'candidate-finder' ? 'Your recommended next step' : 'Account access'}
      </span>
      <Title level={4}>
        {checkoutPlan
          ? 'Sign up to complete purchase'
          : entryPoint === 'candidate-finder'
            ? activeTab === 'signup'
              ? 'Create your ScribeShadow account'
              : 'Sign in to continue'
            : 'Sign in to ScribeShadow'}
      </Title>
      <Text type="secondary">
        {checkoutPlan
          ? 'Create your account to subscribe and receive your credits.'
          : entryPoint === 'candidate-finder'
            ? activeTab === 'signup'
              ? 'Use your included 5,000 Forever Credits toward a translated sample when you are ready.'
              : 'Return to your assessment and continue with your existing books and credits.'
          : 'Access your books, credits, translations, and account settings.'}
      </Text>
    </div>
  );

  return (
    <>
      <Modal
        title={modalTitle}
        visible={isLoginModalVisible}
        onCancel={handleLoginCancel}
        footer={null}
        centered
        width={440}
        className="auth-modal"
      >
        <div className="auth-modal-body">
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          centered
          size="large"
          // Ensure only the active pane is mounted to prevent form collisions and unnecessary re-mounts
          destroyOnHidden
          items={[
            {
              key: 'signin',
              label: 'Sign In',
              children: (
                <Form className="auth-modal-form" form={signInForm} layout="vertical" onFinish={handleFormSubmit}>
                  <Form.Item
                    name="username"
                    rules={[{ validator: validateEmail }]}
                    help={error}
                    validateStatus={error ? 'error' : ''}
                    // Validate on blur to avoid validator firing on each keypress
                    validateTrigger="onBlur"
                    // Sanitize to lowercase/trim without forcing extra renders
                    getValueFromEvent={emailSanitizer}
                  >
                    <Input prefix={<MailOutlined />} placeholder="Email" size="large" />
                  </Form.Item>
                  <Form.Item
                    name="password"
                    rules={[{ required: true, message: 'Please input your password!' }]}
                  >
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="Password"
                      size="large"
                    />
                  </Form.Item>
                  {message && (
                    <Alert
                      message={message}
                      type="info"
                      showIcon
                      className="auth-modal-alert"
                    />
                  )}
                  <Form.Item className="auth-modal-action-row">
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      size="large"
                      block
                    >
                      Sign In
                    </Button>
                  </Form.Item>
                  <div className="auth-modal-secondary-action">
                    <Button type="link" onClick={showResetModal}>
                      Forgot password?
                    </Button>
                  </div>
                </Form>
              ),
            },
            {
              key: 'signup',
              label: 'Sign Up',
              children: (
                <Form
                  className="auth-modal-form"
                  form={signUpForm}
                  layout="vertical"
                  onFinish={handleFormSubmit}
                  onFieldsChange={checkSignUpFormValidity}
                >
                  <Alert
                    message="New users receive 5000 free credits!"
                    type="success"
                    showIcon
                    className="auth-modal-alert"
                  />
                  <Form.Item
                    name="username"
                    rules={[{ validator: validateEmail }]}
                    help={error}
                    validateStatus={error ? 'error' : ''}
                    validateTrigger="onBlur"
                    getValueFromEvent={emailSanitizer}
                  >
                    <Input prefix={<MailOutlined />} placeholder="Email" size="large" />
                  </Form.Item>
                  <Form.Item
                    name="password"
                    rules={[
                      { required: true, message: 'Please input your password!' },
                      { min: 8, message: 'Password must be at least 8 characters!' },
                    ]}
                  >
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="Password (min 8 characters)"
                      size="large"
                    />
                  </Form.Item>
                  <Form.Item
                    name="referralCode"
                    label="Friend's affiliate code (optional)"
                    getValueFromEvent={(e) => sanitizeReferralCode(e?.target?.value ?? '')}
                    className="auth-modal-form-item"
                  >
                    <Input placeholder="Enter affiliate code (optional)" size="large" maxLength={15} />
                  </Form.Item>
                  <Form.Item
                    name="signupSource"
                    label="How did you hear about us?"
                    rules={[{ required: true, message: 'Please tell us how you heard about us.' }]}
                    className="auth-modal-form-item"
                  >
                    <Select
                      placeholder="Select an option"
                      size="large"
                      options={SIGNUP_SOURCE_OPTIONS.map(option => ({ label: option, value: option }))}
                      onChange={(value: string) => setSignupSource(value)}
                    />
                  </Form.Item>
                  {signupSourceAcceptsDetail(signupSource) && (
                    <Form.Item
                      name="signupSourceDetail"
                      label="Which one? (optional)"
                      className="auth-modal-form-item"
                    >
                      <Input
                        placeholder="Name of the conference, podcast, or webinar"
                        size="large"
                        maxLength={200}
                      />
                    </Form.Item>
                  )}
                  <Form.Item
                    name="emailConsent"
                    valuePropName="checked"
                    rules={[
                      {
                        validator: (_, value) =>
                          value
                            ? Promise.resolve()
                            : Promise.reject('You must agree to receive emails to sign up'),
                      },
                    ]}
                    className="auth-modal-form-item"
                  >
                    <Checkbox>
                      <span className="auth-modal-consent-copy">
                        I agree to receive emails from ScribeShadow about updates and
                        features.
                        <a
                          href="https://www.scribeshadow.com/privacy-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="auth-modal-inline-link"
                        >
                          Privacy Policy
                        </a>
                      </span>
                    </Checkbox>
                  </Form.Item>
                  {message && (
                    <Alert
                      message={message}
                      type="info"
                      showIcon
                      className="auth-modal-alert"
                    />
                  )}
                  <Form.Item className="auth-modal-action-row">
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      size="large"
                      block
                      disabled={!isSignUpFormValid}
                    >
                      Create Account
                    </Button>
                  </Form.Item>
                </Form>
              ),
            },
          ]}
        />
        {activeTab === 'signin' && (
          <Alert
            message={
              <span className="auth-modal-switch-copy">
                First time here?{' '}
                <Button type="link" className="auth-modal-inline-button" onClick={() => setActiveTab('signup')}>
                  Create account
                </Button>
                and get 5000 free credits.
              </span>
            }
            type="info"
            className="auth-modal-alert"
          />
        )}
        <Divider className="auth-modal-divider">Or continue with</Divider>
        <div className="auth-modal-provider-row">
          <button
            type="button"
            onClick={handleGoogleSignIn}
            className="auth-modal-google-button"
            disabled={loading}
            aria-busy={loading}
          >
            <GoogleOutlined />
            <img
              src="/btn_google_signin_light_focus_web.png"
              alt="Sign in with Google"
            />
          </button>
        </div>
        </div>
      </Modal>

      {/* Google Sign Up Consent Modal */}
      <Modal
        title="Email Consent Required"
        visible={googleConsentVisible}
        onCancel={() => setGoogleConsentVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setGoogleConsentVisible(false)}>
            Cancel
          </Button>,
          <Button key="agree" type="primary" onClick={handleGoogleSignUpWithConsent} loading={loading}>
            Agree & Continue
          </Button>,
        ]}
        centered
        width={440}
        className="auth-modal"
      >
        <div className="auth-modal-block">
          <span className="auth-modal-kicker">Email consent</span>
          <p>
            To complete your sign up with Google, you must agree to receive emails
            from ScribeShadow.
          </p>
        </div>
        <div className="auth-modal-muted-row">
          <Checkbox checked disabled>
            <span className="auth-modal-consent-copy">
              I agree to receive emails from ScribeShadow about updates and
              features.
            </span>
          </Checkbox>
        </div>
        <p className="auth-modal-helper">
          By clicking "Agree & Continue", you consent to our email
          communications and agree to our{' '}
          <a
            href="https://www.scribeshadow.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
          .
        </p>
      </Modal>
    </>
  );
};

export default SignInModalComponent;
